import styled, { css, keyframes } from 'styled-components';
import PageTop from '../components/pageTop/PageTop';
import { useEffect, useState } from 'react';
import { usePatternContext } from '../../context/PatternContext';
import Footer from '../components/footer/Footer';
import { Invest, Profit } from './myAlgorithm/algorithmType';
import { InitialInvestOptions, InitialProfitOptions } from './myAlgorithm/data';
import { PatternInfoProps } from './myAlgorithm/result/report/data';
import { Region } from '../search/FilterData';
import { useLocation, useNavigate } from 'react-router-dom';
import AnalysisCriteria from './myAlgorithm/pattern/AnalysisCriteria';
import CorePattern from './myAlgorithm/pattern/CorePattern';
import AlgorithmService from '../../service/auction/AlgorithmService';
import { useModal } from '../../context/ModalContext';
import { useUser } from '../../context/UserContext';
import QuotaService from '../../service/quota/Quota';
import { isFinalMonth, isLogin } from '../../utils/auth';
import SaveModal from './myAlgorithm/pattern/SaveModal';
import Toast from '../components/toast/Toast';
import Loading from '../../func/components/loading/LoadingCircle';
import LoadingModal from '../../func/components/loading/LoadingModal';

const MyAlgorithm = () => {
  const navigate = useNavigate();
  const {
    setPIdx,
    investOptions,
    profitOptions,
    setInvestOptions,
    setProfitOptions,
    patternInfo,
    available,
  } = usePatternContext();
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [isSaveDone, setIsSaveDone] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const { modalDispatch } = useModal();
  const { user } = useUser();
  const [createAccess, setCreateAccess] = useState(false);
  const [resultAccess, setResultAccess] = useState(false);
  const [recommendAccess, setRecommendAccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const { state } = useLocation() as {
    state?: { edit: boolean; region: Region };
  };

  const mapPatternToOptions = (
    patternInfo: PatternInfoProps,
    region: Region,
  ) => {
    const invest: Invest = {
      pnu: patternInfo.pnu || '',
      region: region,
      category: patternInfo.category || '',
      categoryDetail: patternInfo.category_detail || '',
      investmentPeriod: patternInfo.investment_period || 'A',
      investmentPurpose: patternInfo.investment_purpose || 'A',
      aging_start_year: patternInfo.aging_start_year || '',
      aging_end_year: patternInfo.aging_end_year || '',
      useArea: patternInfo.zoning ? patternInfo.zoning.split(',') : [],
    };

    const profit: Profit = {
      revenueType: patternInfo.revenue_type || 'A',
      costIncluded: patternInfo.cost_included || 'N',
      investmentType: patternInfo.investment_type || 'A',
      minInvestmentValue: patternInfo.min_investment_value || '',
      maxInvestmentValue: patternInfo.max_investment_value || '',
      investmentRate: patternInfo.investment_rate || '',
      profitRate: patternInfo.profit_rate || 'A',
      customBidCnt: !!(patternInfo.min_bid_cnt || patternInfo.max_bid_cnt),
      minBidCnt: patternInfo.min_bid_cnt || '',
      maxBidCnt: patternInfo.max_bid_cnt || '',
      isRisk: !!patternInfo.risk && patternInfo.risk.length > 0,
      risk: patternInfo.risk ? patternInfo.risk.split(',') : [],
      prcReliability: patternInfo.prc_reliability || 'B',
      auctionDateYears: patternInfo.auction_date_years || '5',
      profitStatus: patternInfo.profit_status || 'B',
      valueStatus: patternInfo.value_status || 'B',
    };

    return { invest, profit };
  };

  useEffect(() => {
    if (state?.edit) {
      const { invest, profit } = mapPatternToOptions(patternInfo, state.region);
      setInvestOptions(invest);
      setProfitOptions(profit);
    } else {
      setPIdx(0);
      setInvestOptions(InitialInvestOptions);
      setProfitOptions(InitialProfitOptions);
    }
  }, [state]);

  const getValue = (list: any[], type: string) => {
    const f = list.find((x) => x.type === type);
    return { count: f?.count ?? 0, limit: f?.limit ?? 0 };
  };

  const checkQuota = async () => {
    if (!isLogin()) return;
    try {
      const res = await QuotaService.count();
      const create = getValue(res.data, 'D');
      const result = getValue(res.data, 'I');
      const recommend = getValue(res.data, 'E');
      setCreateAccess(create.limit > create.count);
      setResultAccess(result.limit > result.count);
      setRecommendAccess(recommend.limit > recommend.count);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    checkQuota();
  }, []);

  const serviceQuota = async (type: string, algorithmIdx: number) => {
    try {
      const data = {
        type: type,
        algorithmIdx: algorithmIdx,
      };
      await QuotaService.use(data);
    } catch (err) {
      console.log(err, '발생');
    }
  };

  const checkAccess = (access: boolean) => {
    if (!isLogin()) {
      modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
      return false;
    }
    const grade = user?.plan_grade;
    if (!grade) {
      modalDispatch({ type: 'USER_ALERT', payload: 'free' });
      return false;
    }
    if (!access) {
      if (isFinalMonth(user?.plan_end)) {
        modalDispatch({
          type: 'USER_ALERT',
          payload: 'quotaDepletedExpiring',
        });
      } else {
        modalDispatch({
          type: 'USER_ALERT',
          payload: 'quotaDepleted',
        });
      }
      return false;
    }
    return true;
  };

  const createPattern = async () => {
    if (!checkAccess(createAccess)) return 0;
    setLoading(true);
    try {
      const payload = {
        pnu: investOptions.pnu,
        category: investOptions.category,
        category_detail: investOptions.categoryDetail,
        investment_period: investOptions.investmentPeriod,
        investment_purpose: investOptions.investmentPurpose,
        aging_start_year: investOptions.aging_start_year,
        aging_end_year: investOptions.aging_end_year,
        zoning: investOptions.useArea.join(','),
        revenue_type: profitOptions.revenueType,
        cost_included: profitOptions.costIncluded,
        investment_type: profitOptions.investmentType,
        min_investment_value: Number(profitOptions.minInvestmentValue) * 10000,
        max_investment_value: Number(profitOptions.maxInvestmentValue) * 10000,
        investment_rate: profitOptions.investmentRate,
        profit_rate: profitOptions.profitRate,
        min_bid_cnt: profitOptions.minBidCnt,
        max_bid_cnt: profitOptions.maxBidCnt,
        risk: profitOptions.risk.join(','),
        prc_reliability: profitOptions.prcReliability,
        auction_date_years: profitOptions.auctionDateYears,
        profit_status: profitOptions.profitStatus,
        value_status: profitOptions.valueStatus,
      };
      const res = await AlgorithmService.createAlgorithm(payload);

      if (res.status === 'success') {
        if (res.data) {
          setPIdx(res.data);
        }
        serviceQuota('D', res.data);
      }
      return res.data;
    } catch (err: any) {
      console.error('❌ 알고리즘 생성 실패');
      console.error('에러 객체:', err);
      console.error('에러 응답 데이터:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const getResult = async (iidx: number) => {
    try {
      const res = await AlgorithmService.getAlgorithmAnalysis(iidx);
      return res.data;
    } catch (err) {
      console.log(err);
    }
  };
  const handleAnalyzeButton = async () => {
    const iidx = await createPattern();
    if (!iidx) return;
    if (!checkAccess(resultAccess)) return;
    navigate(`/patternResult/${iidx}`);
  };

  const handleSaveButton = () => {
    setOpenSaveModal(true);
  };

  const handleSearchRecommend = async (patternInfo: any) => {
    if (!checkAccess(recommendAccess)) return;
    navigate('/AiRecommendation', {
      state: {
        selectedItem: patternInfo,
      },
    });
  };

  useEffect(() => {
    if (!isSaveDone) return;
    setShowToast(true);
  }, [isSaveDone]);

  return (
    <Container>
      <PageTop text={'알고리즘 만들기'} />
      <Inner>
        <AnalysisCriteria />
        <CorePattern />
      </Inner>
      <Footer />
      <SelectedArea>
        <BottomButtonBox>
          <SearchButton $disabled={!available} onClick={handleAnalyzeButton}>
            알고리즘 수익성 분석
          </SearchButton>
        </BottomButtonBox>
      </SelectedArea>
      {openSaveModal && (
        <SaveModal
          createPattern={createPattern}
          getResult={getResult}
          onClose={() => setOpenSaveModal(false)}
          onDone={() => setIsSaveDone(true)}
          onSearchRecommend={handleSearchRecommend}
        />
      )}
      {showToast && (
        <Toast
          message="알고리즘 저장 완료"
          position={103}
          onClose={() => {
            setShowToast(false);
            setIsSaveDone(false);
          }}
        />
      )}
      {loading && <LoadingModal />}
    </Container>
  );
};
export default MyAlgorithm;

const Container = styled.div`
  max-width: 900px;
  min-height: 100vh;
  margin: 0 auto;
  box-shadow: 0 0 20px hsla(0, 0%, 51%, 0.15);
  position: relative;
  background-color: #f7f8fc;
`;

const Inner = styled.div`
  display: flex;
  padding: 24px 16px 72px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
`;

const SelectedArea = styled.div`
  position: fixed;
  bottom: 0;
  z-index: 1000;

  display: flex;
  width: 100%;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-top: 1px solid #e5e8ea;
  background: #fff;
`;

const BottomButtonBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
`;

const Button = styled.button<{ $disabled?: boolean }>`
  outline: none;
  border: none;

  display: flex;
  padding: 12px 28px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  text-align: center;
  font-size: 15px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;

  ${({ $disabled }) =>
    $disabled &&
    css`
      && {
        background: #f7f7f8;
        color: #37383c29;
        pointer-events: none;
      }
    `}
`;

const AnalysisButton = styled(Button)`
  background: rgba(112, 115, 124, 0.08);
  color: #616161;
`;

const SearchButton = styled(Button)`
  flex: 1 0 0;
  background: #008aff;
  color: #fff;
`;
